import { calculateSalaryTax } from './taxCalculations';

const calculateGradeDesc = (val, thresholds) => {
    if (val >= thresholds[0]) return "A";
    if (val >= thresholds[1]) return "B";
    if (val >= thresholds[2]) return "C";
    if (val >= thresholds[3]) return "D";
    return "F";
};

const calculateGradeAsc = (val, thresholds) => {
    if (val <= thresholds[0]) return "A";
    if (val <= thresholds[1]) return "B";
    if (val <= thresholds[2]) return "C";
    if (val <= thresholds[3]) return "D";
    return "F";
};

const getLinearScore = (val, thresholds, isInverted = false) => {
    const points = [thresholds[0], thresholds[1], thresholds[2], thresholds[3]];

    if (!isInverted) {
        if (val >= points[0]) return Math.min(100, 90 + ((val - points[0]) / (points[0] || 1)) * 10);
        if (val >= points[1]) return 70 + ((val - points[1]) / (points[0] - points[1] || 1)) * 15;
        if (val >= points[2]) return 55 + ((val - points[2]) / (points[1] - points[2] || 1)) * 15;
        if (val >= points[3]) return 30 + ((val - points[3]) / (points[2] - points[3] || 1)) * 25;
        return Math.max(0, (val / (points[3] || 1)) * 30);
    } else {
        if (val <= points[0]) return 100;
        if (val <= points[1]) return 85 + ((points[1] - val) / (points[1] - points[0] || 1)) * 15;
        if (val <= points[2]) return 70 + ((points[2] - val) / (points[2] - points[1] || 1)) * 15;
        if (val <= points[3]) return 55 + ((points[3] - val) / (points[3] - points[2] || 1)) * 15;
        return Math.max(0, 30 - ((val - points[3]) / (points[3] || 1)) * 30);
    }
};

const safeParse = (v) => {
    if (typeof v === 'number') return v;
    if (!v) return 0;
    return parseFloat(v.toString().replace(/[₹, \s]/g, '')) || 0;
};

export const calculateFinancialDiagnosis = (data) => {
    const personalInfo = data.personalInfo || {};
    const isCouple = personalInfo.familyType === 'couple';
    const jointSalary = data.jointSalary || {};

    let salaryIncome = data.salaryIncome || {};
    let salaryExpenses = data.salaryExpenses || {};
    let salaryInvestment = data.salaryInvestment || {};

    if (isCouple) {
        const jC = jointSalary.sectionC || {};
        const jD = jointSalary.sectionD || {};
        const jE = jointSalary.sectionE || {};
        const jF = jointSalary.sectionF || {};
        const jG = jointSalary.sectionG || {};

        salaryIncome = {
            rentEMI: safeParse(jC.rent) || 0,
            homeLoanEMI: safeParse(jC.homeLoanEMI) || safeParse(jF.homeLoanEMI) || 0,
            personalLoanEMI: safeParse(jC.personalLoanEMI) || safeParse(jF.personalLoanEMI) || 0,
            carLoanEMI: safeParse(jC.carLoanEMI) || safeParse(jF.carLoanEMI) || 0,
            electricity: 0,
            water: 0,
            gas: 0,
            internet: 0,
            broadband: 0,
            ottSubscriptions: 0,
            cable: 0,
            otherLoanEMI: 0,
            lifeInsurance: safeParse(jE.husbandLifePremium) + safeParse(jE.wifeLifePremium),
            healthInsurance: safeParse(jE.husbandHealthPremium) + safeParse(jE.wifeHealthPremium),
            lifeInsuranceCover: safeParse(jE.husbandLifeCover) + safeParse(jE.wifeLifeCover),
            healthInsuranceCover: safeParse(jE.husbandHealthCover) + safeParse(jE.wifeHealthCover),
            epf: safeParse(jD.husbandEPF) + safeParse(jD.wifeEPF),
            ppf: safeParse(jD.husbandPPF) + safeParse(jD.wifePPF),
            nps_80ccd2: safeParse(jD.husbandNPS) + safeParse(jD.wifeNPS),
            sukanyaSamriddhi: safeParse(jD.husbandOtherInvest) + safeParse(jD.wifeOtherInvest),
            homeLoanOutstanding: safeParse(jF.homeLoanOutstanding) || 0,
            personalLoanOutstanding: safeParse(jF.personalLoanOutstanding) || 0,
            carLoanOutstanding: safeParse(jF.carLoanOutstanding) || 0,
            creditCardOutstanding: safeParse(jF.creditCardOutstanding) || 0,
            creditCardEMI: safeParse(jF.creditCardOutstanding) ? (safeParse(jF.creditCardOutstanding) * 0.05) : 0,
        };

        salaryExpenses = {
            groceries: safeParse(jC.groceries) || 0,
            schoolFees: safeParse(jC.schoolFees) || 0,
            transport: safeParse(jC.transport) || 0,
            otherExpenses: safeParse(jC.otherExpenses) || 0,
        };

        salaryInvestment = {
            diningOut: safeParse(jC.dining) || 0,
            shopping: safeParse(jC.shopping) || 0,
            mutualFunds: safeParse(jD.husbandMutualFund) + safeParse(jD.wifeMutualFund),
            elss: safeParse(jD.husbandELSS) + safeParse(jD.wifeELSS),
            fixedDeposits: safeParse(jD.husbandFD) + safeParse(jD.wifeFD),
            emergencyFund: safeParse(jG.emergencyFund) || 0,
        };
    }

    // Helper to sum all numeric values in an object
    const sumAll = (obj) => Object.values(obj || {}).reduce((acc, val) => acc + (safeParse(val) || 0), 0);

    // 1. Total Income
    const monthlyGrossSalary = isCouple 
        ? (safeParse(jointSalary.sectionB?.husbandSalary) + safeParse(jointSalary.sectionB?.wifeSalary))
        : (safeParse(personalInfo?.monthlySalary) || 0);
    const otherIncome = isCouple
        ? (safeParse(jointSalary.sectionB?.husbandOtherIncome) + safeParse(jointSalary.sectionB?.wifeOtherIncome))
        : (safeParse(personalInfo?.otherIncomeAmount) || 0);
    const totalMonthlyIncome = monthlyGrossSalary + otherIncome;
    const annualGrossSalary = totalMonthlyIncome * 12;

    // 2. Essential Expenses (Needs - Target < 50%)
    // USER SPECIFIC FORMULA: Rent/EMI, Groceries, Utilities, Transport, Personal Loan EMI, Insurance Premium

    const rentEMI = (safeParse(salaryIncome.rentEMI) || 0) + (safeParse(salaryIncome.homeLoanEMI) || 0);
    const groceries = (safeParse(salaryExpenses.groceries) || 0) + (safeParse(salaryExpenses.vegetables) || 0) + (safeParse(salaryExpenses.dairyProducts) || 0) + (safeParse(salaryExpenses.householdSupplies) || 0);
    const medical = (safeParse(salaryExpenses.doctorConsultations) || 0) + (safeParse(salaryExpenses.medicines) || 0) + (safeParse(salaryExpenses.diagnosticTests) || 0) + (safeParse(salaryExpenses.dentalCare) || 0) + (safeParse(salaryExpenses.eyeCare) || 0);
    const education = (safeParse(salaryExpenses.schoolFees) || 0) + (safeParse(salaryExpenses.booksStationery) || 0) + (safeParse(salaryExpenses.tuitionClasses) || 0) + (safeParse(salaryExpenses.extracurricular) || 0);
    const utilities = (safeParse(salaryIncome.electricity) || 0) +
        (safeParse(salaryIncome.water) || 0) +
        (safeParse(salaryIncome.gas) || 0) +
        (safeParse(salaryIncome.internet) || 0) +
        (safeParse(salaryIncome.broadband) || 0) +
        (safeParse(salaryIncome.ottSubscriptions) || 0) +
        (safeParse(salaryIncome.cable) || 0) + medical + education;
    const transport = (safeParse(salaryExpenses.fuelDiesel) || 0) + (safeParse(salaryExpenses.publicTransport) || 0) + (safeParse(salaryExpenses.taxiCabUber) || 0) + (safeParse(salaryExpenses.vehicleMaintenance) || 0) + (safeParse(salaryExpenses.parkingCharges) || 0) + (safeParse(salaryExpenses.tollCharges) || 0);
    const personalLoanEMI = safeParse(salaryIncome.personalLoanEMI) || 0;
    const otherLoanEMI = safeParse(salaryIncome.otherLoanEMI) || 0;
    const lifeInsurancePremium = safeParse(salaryIncome.lifeInsurance) || 0;
    const healthInsurancePremium = safeParse(salaryIncome.healthInsurance) || 0;
    const insurancePremium = lifeInsurancePremium + healthInsurancePremium;

    // This is the restricted total for the "Expense to Income Ratio" as requested by user
    const totalEssentialExpenses = rentEMI + groceries + utilities + transport + personalLoanEMI + insurancePremium + otherLoanEMI;
    const essentialRatio = (totalMonthlyIncome > 0) ? Math.min(100, (totalEssentialExpenses / totalMonthlyIncome) * 100) : 0;

    // 3. Lifestyle Expenses (Wants - Target 10-15%)
    // Only explicitly entered DISCRETIONARY fields count — no estimation or inference.
    const lifestyleInvKeys = [
        // Entertainment & leisure (Part 3 - salaryInvestment)
        'entertainment', 'diningOut', 'moviesTheater', 'sportsHobbies', 'clubMemberships',
        // Shopping (Part 3)
        'shopping', 'electronics', 'furnitureDecor', 'apparelAccessories', 'giftsDonations',
        // Travel (Part 3)
        'travel', 'vacationPlanning', 'dailyOfficeCommute',
        // Family (Part 3)
        'parentingSupport', 'childrenAllowance', 'domesticHelp',
        // Social & occasions (Part 3)
        'weddingContributions', 'festivalExpenses', 'religiousDonations', 'charityNGO',
        'birthdayCelebrations', 'anniversaryCelebrations', 'familyFunctions',
        // Professional extras (Part 3)
        'professionalCourses', 'certificationFees', 'booksJournals', 'workFromHome',
        'professionalMembership', 'officeCabTravel', 'businessMeals'
    ];

    // Genuine discretionary fields from Part 2 (salaryExpenses) — not medical/education
    const lifestyleExpKeys = [
        'salonBarber', 'cosmetics', 'clothingFootwear',  // Personal care
        // 'householdSupplies' moved to groceries
        // 'vehicleMaintenance', 'parkingCharges', 'tollCharges' moved to transport
    ];
    const totalLifestyleExpenses =
        lifestyleInvKeys.reduce((acc, key) => acc + (safeParse(salaryInvestment[key]) || 0), 0) +
        lifestyleExpKeys.reduce((acc, key) => acc + (safeParse(salaryExpenses[key]) || 0), 0);

    const lifestyleRatio = (totalMonthlyIncome > 0)
        ? Math.min(100, (totalLifestyleExpenses / totalMonthlyIncome) * 100)
        : 0;



    // 4. Savings & Investments (Savings - Target > 20%)
    const explicitSaving = [
        'epf', 'vpf', 'ppf', 'nps_80ccd2', 'sukanyaSamriddhi' // From Part 1
    ].reduce((acc, key) => acc + (safeParse(salaryIncome[key]) || 0), 0) +
        [
            'elss', 'mutualFunds', 'stocksEquity', 'recurringDeposits', 'fixedDeposits', 'goldBonds', 'realEstate',
            'emergencyFund', 'liquidFund' // From Part 3
        ].reduce((acc, key) => acc + (safeParse(salaryInvestment[key]) || 0), 0);

    const totalMonthlyInvestments = explicitSaving;

    // 5. Debt Management
    const totalEMI = (safeParse(salaryIncome.homeLoanEMI) || 0) +
        (safeParse(salaryIncome.carLoanEMI) || 0) +
        (safeParse(salaryIncome.personalLoanEMI) || 0) +
        (safeParse(salaryIncome.educationLoanEMI) || 0) +
        (safeParse(salaryIncome.creditCardEMI) || 0) +
        (safeParse(salaryIncome.otherLoanEMI) || 0);

    // Total Outflow (Includes all fields for accurate surplus, but EIR uses restricted list)
    // To get actual outflow, we need to sum everything. 
    // We already have totalEssentialExpenses (restricted), totalLifestyleExpenses, totalMonthlyInvestments, totalEMI.
    // However, some fields like societyMaintenance, propertyTax, motorInsurance etc are NOT in totalEssentialExpenses (restricted).
    // Let's calculate a more comprehensive "Total Outflow" for surplus calculation.

    // Comprehensive Outflow = sum of all fields in salaryIncome, salaryExpenses, salaryInvestment
    const allIncomeExp = sumAll(salaryIncome);
    const allLivingExp = sumAll(salaryExpenses);
    const allInvestmentExp = sumAll(salaryInvestment);

    const totalOutflow = allIncomeExp + allLivingExp + allInvestmentExp;
    const surplus = Math.max(0, totalMonthlyIncome - totalOutflow);
    // Explicit savings rate (as per case study, don't auto-bundle surplus into savings rate)
    const savingsRate = (totalMonthlyIncome > 0) ? Math.min(100, (totalMonthlyInvestments / totalMonthlyIncome) * 100) : 0;

    const dtiRatio = (totalMonthlyIncome > 0) ? Math.min(100, (totalEMI / totalMonthlyIncome) * 100) : 0;

    // 6. Emergency Fund & Insurance
    // currentEmergency is current corpus/contribution
    const currentEmergency = (safeParse(salaryInvestment.liquidFund) || 0) +
        (safeParse(salaryInvestment.emergencyFund) || 0) +
        (safeParse(salaryInvestment.fixedDeposits) || 0);
    const targetEmergencyBasis = Math.max(totalEssentialExpenses, monthlyGrossSalary * 0.5);
    const targetEmergency = targetEmergencyBasis * 6;
    const emergencyFundRatio = targetEmergency > 0 ? Math.min(100, (currentEmergency / targetEmergency) * 100) : 0;

    const currentLifeCover = safeParse(salaryIncome.lifeInsuranceCover) || safeParse(data.landGold?.lifeInsuranceCover) || safeParse(salaryIncome.lifeInsuranceAmount) || 0;
    const idealLifeCover = annualGrossSalary * 10;
    const insuranceRatio = idealLifeCover > 0 ? Math.min(100, (currentLifeCover / idealLifeCover) * 100) : 0;

    // DIAGNOSIS LABELS
    let essentialDiag = { result: "Healthy", color: "green" };
    if (essentialRatio > 55) essentialDiag = { result: "Critical", color: "red" };
    else if (essentialRatio > 50) essentialDiag = { result: "High", color: "orange" };
    else if (essentialRatio < 20) essentialDiag = { result: "Low", color: "blue" };

    let lifestyleDiag = { result: "Healthy", color: "green" };
    if (lifestyleRatio > 25) lifestyleDiag = { result: "Critical", color: "red" };
    else if (lifestyleRatio > 15) lifestyleDiag = { result: "High", color: "orange" };

    let savingsDiag = { result: "Healthy", color: "green" };
    if (savingsRate < 10) savingsDiag = { result: "Wait & Watch", color: "red" };
    else if (savingsRate < 20) savingsDiag = { result: "Average", color: "orange" };

    let emergencyDiag = { result: "Adequate", color: "green" };
    if (emergencyFundRatio < 10) emergencyDiag = { result: "Gap", color: "red" }; // Adjusted threshold to show building
    else if (emergencyFundRatio < 100) emergencyDiag = { result: "Building", color: "orange" };

    let debtDiag = { result: "Healthy", color: "green" };
    if (dtiRatio > 40) debtDiag = { result: "High Risk", color: "red" };
    else if (dtiRatio > 30) debtDiag = { result: "Watch", color: "orange" };

    let insuranceDiag = { result: "Adequate", color: "green" };
    if (insuranceRatio < 40) insuranceDiag = { result: "Major Gap", color: "red" };
    else if (insuranceRatio < 90) insuranceDiag = { result: "Gap", color: "orange" };

    // GRADING
    // --- 1. GRADES & SCORES PRE-CALCULATION ---
    const expensesRatio = (totalMonthlyIncome > 0) ? ((totalEssentialExpenses + totalLifestyleExpenses) / totalMonthlyIncome) * 100 : 0;
    const efcMonths = totalEssentialExpenses > 0 ? (currentEmergency / totalEssentialExpenses) : 0;
    const icaMulti = annualGrossSalary > 0 ? (currentLifeCover / annualGrossSalary) : 0;

    const sGrade = calculateGradeDesc(savingsRate, [25, 20, 15, 10]);
    const sScore = getLinearScore(savingsRate, [25, 20, 15, 10]);

    const eirGrade = calculateGradeAsc(expensesRatio, [50, 60, 70, 80]);
    const eirScore = getLinearScore(expensesRatio, [50, 60, 70, 80], true);

    const dGrade = calculateGradeAsc(dtiRatio, [15, 25, 35, 45]);
    const dScore = getLinearScore(dtiRatio, [15, 25, 35, 45], true);

    const efcGrade = calculateGradeDesc(efcMonths, [9, 6, 3, 1]);
    const efcScore = getLinearScore(efcMonths, [9, 6, 3, 1]);

    const icaGrade = calculateGradeDesc(icaMulti, [15, 10, 5, 1]);
    const icaScore = getLinearScore(icaMulti, [15, 10, 5, 1]);
    const icaCalculation = `₹${(currentLifeCover).toLocaleString('en-IN')} ÷ ₹${(annualGrossSalary).toLocaleString('en-IN')}`;

    const lifestyleGrade = calculateGradeAsc(lifestyleRatio, [15, 20, 25, 30]);
    const lifestyleScore = getLinearScore(lifestyleRatio, [15, 20, 25, 30], true);

    // Approximate TES (Tax Efficiency)
    const maxValuableDeductions = 150000 + 25000;
    const usedDeductions = Math.min(150000, explicitSaving * 12) + Math.min(25000, (safeParse(salaryIncome.healthInsurance) || 0) * 12);
    const tesPercent = Math.min(100, (usedDeductions / maxValuableDeductions) * 100);
    const tesGrade = calculateGradeDesc(tesPercent, [90, 75, 50, 25]);
    const tesScore = getLinearScore(tesPercent, [90, 75, 50, 25]);

    // Investment Diversification Score (IDS)
    const investmentCount = Object.keys(salaryInvestment).filter(k => safeParse(salaryInvestment[k]) > 0).length;
    let idsGrade = "C";
    let idsValue = "Moderate";
    if (investmentCount >= 4) { idsGrade = "A"; idsValue = "Well Diversified"; }
    else if (investmentCount === 3) { idsGrade = "B"; idsValue = "Moderate"; }
    else if (investmentCount === 2) { idsGrade = "C"; idsValue = "Some Diversification"; }
    else if (investmentCount === 1) { idsGrade = "D"; idsValue = "Limited"; }
    else { idsGrade = "F"; idsValue = "No Diversification"; }

    // Retirement Readiness (RRS)
    const retirementTotal = (safeParse(salaryIncome.epf) || 0) * 12 + (safeParse(salaryIncome.ppf) || 0) * 12 + (safeParse(salaryIncome.nps_80ccd2) || 0) * 12;
    const rrsProgress = totalMonthlyIncome > 0 && retirementTotal > 0 ? (retirementTotal / (totalMonthlyIncome * 0.10 * 12)) * 10 : 0;
    const rrsGrade = calculateGradeDesc(rrsProgress, [80, 60, 40, 20]);
    const rrsScore = Math.min(100, Math.round(rrsProgress * 10));

    // --- 2. STATUS LABELS ---
    const icaStatusVal = icaGrade === 'A' ? "Adequate" : (icaGrade === 'B' ? "Partial Gap" : "Critical");
    const efcStatusVal = efcGrade === 'A' ? "Adequate" : (efcGrade === 'B' ? "Building" : "Critical");
    const dtiStatusVal = dGrade === 'A' ? "Excellent" : (dGrade === 'B' ? "Manageable" : "High Debt");
    const lifestyleStatusVal = lifestyleRatio <= 15 ? "Excellent Control" : "Overspending";
    const essentialStatusVal = essentialRatio <= 50 ? "Excellent" : "Very High";
    const savingsStatusVal = sGrade === 'A' ? "Excellent" : (sGrade === 'B' ? "Good" : "Critical");

    const getStatus = (grade) => {
        const statuses = { A: "Excellent", B: "Good", C: "Fair", D: "Needs Work", F: "Critical" };
        return statuses[grade] || "Critical";
    };

    const getPriority = (grade) => {
        const priorities = { A: "LOW", B: "MEDIUM", C: "HIGH", D: "URGENT", F: "CRITICAL" };
        return priorities[grade] || "CRITICAL";
    };

    const overallScore = Math.round((sScore + eirScore + dScore + efcScore + icaScore + tesScore) / 6);

    // --- 3. RECOMMENDATIONS ---
    const currentAssets = (safeParse(data?.salaryInvestment?.currentEquityValue) || 0) +
        (safeParse(data?.salaryInvestment?.currentDebtValue) || 0) +
        (safeParse(data?.salaryInvestment?.currentGoldValue) || 0) +
        (safeParse(data?.salaryIncome?.epfBalance) || 0) +
        (safeParse(data?.salaryIncome?.ppfBalance) || 0) +
        currentEmergency;

    const currentLiabilities = (safeParse(data?.salaryIncome?.homeLoanOutstanding) || 0) +
        (safeParse(data?.salaryIncome?.personalLoanOutstanding) || 0) +
        (safeParse(data?.salaryIncome?.carLoanOutstanding) || 0);

    const netWorth = currentAssets - currentLiabilities;

    // RECOMMENDATIONS
    const recommendations = [];
    if (insuranceRatio < 90) {
        recommendations.push({
            category: "Insurance", priority: "High",
            action: `Enhance Life Cover to ₹${(idealLifeCover / 10000000).toFixed(2)} Cr`,
            details: `Maintain 10x of your annual income as pure term insurance to protect family lifestyle.`
        });
    }
    if (emergencyFundRatio < 100) {
        recommendations.push({
            category: "Safety Net", priority: "High",
            action: `Build Emergency Reserve of ₹${Math.round(targetEmergency).toLocaleString()}`,
            details: `Keep 6 months of total expenses in a separate liquid account for unforeseen events.`
        });
    }
    if (savingsRate < 20) {
        recommendations.push({
            category: "Wealth", priority: "Medium",
            action: "Increase Monthly SavingsTarget",
            details: `Redirect discretionary spending to boost savings rate from ${savingsRate.toFixed(1)}% to at least 20%.`
        });
    }
    if (dtiRatio > 35) {
        recommendations.push({
            category: "Debt", priority: "High",
            action: "Aggressive Loan Prepayment",
            details: `Your debt obligation is above healthy levels. Prioritize closing high-interest unsecured loans.`
        });
    }

    // --- TAX CALCULATION (FY 2024-25 / 2025-26 Budget 2024) ---
    const hraExemption = safeParse(data.taxDetails?.hraExemption) || 0;
    const taxResults = calculateSalaryTax({
        annualGross: annualGrossSalary,
        ded80C: explicitSaving * 12 + (safeParse(salaryIncome.homeLoanEMI) || 0) * 12 * 0.40, // Sum assured plus loan principal guess
        ded80D: (safeParse(salaryIncome.healthInsurance) || 0) * 12,
        hraExemption,
        homeLoanInterest: (safeParse(salaryIncome.homeLoanEMI) || 0) * 12 * 0.60
    });

    return {
        metrics: {
            totalMonthlyIncome,
            annualGrossSalary,
            totalEssentialExpenses, // Restricted list per user request
            totalLifestyleExpenses,
            totalMonthlyInvestments,
            essentialRatio,
            lifestyleRatio,
            savingsRate,
            dtiRatio,
            efcMonths,
            icaMulti,
            currentEmergency,
            targetEmergency,
            emergencyFundRatio,
            currentLifeCover,
            idealLifeCover,
            emergencyFundTarget: targetEmergency,
            totalActualSavings: totalMonthlyInvestments,
            monthlySurplus: surplus,
            expensesRatio,
            tesPercent,
            rrsScore,
            totalExpenses: totalEssentialExpenses + totalLifestyleExpenses,
            healthInsuranceCover: safeParse(salaryIncome.healthInsuranceCover) || 0,
            taxBenefits80D: Math.min(25000, (safeParse(salaryIncome.healthInsurance) || 0) * 12) + Math.min(50000, (safeParse(salaryIncome.parentHealthInsurance) || 0) * 12),
            epfMonthly: safeParse(salaryIncome.epf) || 0,
            ppfMonthly: safeParse(salaryIncome.ppf) || 0,
            homeLoanEMI: safeParse(salaryIncome.homeLoanEMI) || 0,
            homeLoanPrincipalAnnual: (safeParse(salaryIncome.homeLoanEMI) || 0) * 12 * 0.40,
            npsMonthly: (safeParse(salaryIncome.nps_80ccd2) || 0) + (safeParse(data.salaryInvestment?.npsTier1) || 0),
            sipMonthly: safeParse(salaryInvestment.mutualFunds) || 0,
            emergencyFundMonthly: (safeParse(salaryInvestment.emergencyFund) || 0) + (safeParse(salaryInvestment.liquidFund) || 0),
            homeLoanOutstanding: safeParse(salaryIncome.homeLoanOutstanding) || 0,
            personalLoanOutstanding: safeParse(data.landGold?.personalLoanOutstanding) || safeParse(salaryIncome.personalLoanOutstanding) || 0,
            personalLoanRate: safeParse(data.landGold?.personalLoanRate) || 14,
            totalEMI,
            lifeInsurancePremium,
            healthInsurancePremium,
            healthInsuranceCover: safeParse(salaryIncome.healthInsuranceCover) || safeParse(data.landGold?.healthInsuranceCover) || safeParse(salaryIncome.healthInsuranceAmount) || 0,
            lifeInsuranceCover: safeParse(salaryIncome.lifeInsuranceCover) || safeParse(data.landGold?.lifeInsuranceCover) || safeParse(salaryIncome.lifeInsuranceAmount) || 0,
            currentAssets,
            currentLiabilities,
            netWorth,
            tax: taxResults
        },
        diagnosis: {
            sr: {
                value: savingsRate.toFixed(1) + "%",
                grade: sGrade,
                status: getStatus(sGrade),
                score: sScore,
                benchmark: `Target:\n>25%`,
                priority: getPriority(sGrade)
            },
            eir: {
                value: essentialRatio.toFixed(1) + "%",
                grade: eirGrade,
                status: getStatus(eirGrade),
                score: eirScore,
                benchmark: `Healthy:\n<50%`,
                priority: getPriority(eirGrade)
            },
            dti: {
                value: dtiRatio.toFixed(1) + "%",
                grade: dGrade,
                status: getStatus(dGrade),
                score: dScore,
                benchmark: `Limit:\n<35%`,
                priority: getPriority(dGrade)
            },
            efc: {
                value: efcMonths.toFixed(1) + " months",
                grade: efcGrade,
                status: getStatus(efcGrade),
                score: efcScore,
                benchmark: `Target:\n₹${(Math.round(Math.max(totalEssentialExpenses, monthlyGrossSalary * 0.5) * 6 / 10000) / 10).toFixed(1)}L (6m)`,
                priority: getPriority(efcGrade)
            },
            ica: {
                value: icaMulti.toFixed(1) + "x",
                grade: icaGrade,
                status: getStatus(icaGrade),
                score: icaScore,
                calculation: icaCalculation,
                benchmark: `Target Range:\n₹${(annualGrossSalary * 10 / 100000).toFixed(1)}L - ₹${(annualGrossSalary * 15 / 100000).toFixed(1)}L\n(10-15x)`,
                priority: getPriority(icaGrade)
            },
            lifestyle: {
                value: lifestyleRatio.toFixed(1) + "%",
                grade: lifestyleGrade,
                status: getStatus(lifestyleGrade),
                score: lifestyleScore,
                benchmark: `Healthy:\n10-15%`,
                priority: getPriority(lifestyleGrade)
            },
            tes: { value: tesPercent.toFixed(0) + "%", grade: tesGrade, status: getStatus(tesGrade), score: tesScore, benchmark: "Target: >80%", priority: getPriority(tesGrade) },
            ids: { value: idsValue, grade: idsGrade, status: getStatus(idsGrade), score: 65, benchmark: "Basis: Diversify", priority: getPriority(idsGrade) },
            rrs: { value: rrsProgress.toFixed(1) + "%", grade: rrsGrade, status: getStatus(rrsGrade), score: rrsScore, benchmark: "Progress: Tracked", priority: getPriority(rrsGrade) },

            // Summary objects for other UI parts if needed (keeping them distinct)
            summary: {
                essential: { result: essentialDiag.result, color: essentialDiag.color, value: essentialRatio.toFixed(1) + "%" },
                lifestyle: { result: lifestyleDiag.result, color: lifestyleDiag.color, value: lifestyleRatio.toFixed(1) + "%" },
                savings: { result: savingsDiag.result, color: savingsDiag.color, value: savingsRate.toFixed(1) + "%" },
                emergency: {
                    result: emergencyDiag.result,
                    color: emergencyDiag.color,
                    value: (currentEmergency > 0 && emergencyFundRatio < 10) ? `₹${currentEmergency.toLocaleString()} Monthly` : emergencyFundRatio.toFixed(0) + "% Funded"
                },
                debt: { result: debtDiag.result, color: debtDiag.color, value: dtiRatio.toFixed(1) + "%" },
                insurance: { result: insuranceDiag.result, color: insuranceDiag.color, value: icaMulti.toFixed(1) + "x Income" }
            }
        },
        grades: {
            savings: sGrade,
            tax: tesGrade,
            insurance: icaGrade,
            debt: dGrade,
            retirement: rrsGrade,
            emergency: efcGrade,
            overall: overallScore
        },
        recommendations
    };
};

/**
 * Business Diagnosis Engine
 * Based on Manufacturing/Trading Business Benchmarks (e.g. Dairy Case Study)
 */
export const calculateBusinessDiagnosis = (data) => {
    const bizData = data.businessDetails || {};
    const bizExp = data.businessExpenses || {};

    const currentAssets = (safeParse(bizData.cashInHand) || 0) + (safeParse(bizData.bankBalance) || 0) + (safeParse(bizData.investments) || 0);
    const currentLiabilities = (safeParse(bizData.outstandingLoans) || 0);
    const netWorth = currentAssets - currentLiabilities;

    const turnover = safeParse(bizData.grossTurnover) || 0;
    const monthlyRevenue = turnover > 0 ? turnover / 12 : 0;

    // 1. COGS (Direct Expenses)
    const rawMaterial = safeParse(bizData.rawMaterialPurchases) || 0;
    const directLabour = safeParse(bizData.directLabourWages) || 0;
    const mfgOverheads = safeParse(bizData.manufacturingOverheads) || 0;
    const powerFuel = safeParse(bizData.powerFuel) || 0;
    const packing = safeParse(bizData.packagingMaterial) || 0;
    const jobWork = safeParse(bizData.jobWorkCharges) || 0;
    const consumables = safeParse(bizData.consumableStores) || 0;
    const freightIn = safeParse(bizData.freightCartageInward) || 0;

    const totalCOGS = rawMaterial + directLabour + mfgOverheads + powerFuel + packing + jobWork + consumables + freightIn;
    const grossProfit = turnover - totalCOGS;
    const grossMargin = turnover > 0 ? (grossProfit / turnover) * 100 : 0;

    // 2. Operating Expenses (OpEx)
    const distribution = safeParse(bizData.distributionTransport) || 0;
    const salaries = safeParse(bizData.salariesWages) || 0;
    const admin = (safeParse(bizData.officeRent) || 0) + (safeParse(bizData.internetTelephone) || 0) + (safeParse(bizData.printingStationery) || 0);
    const marketing = (safeParse(bizData.advertisingPromotion) || 0) + (safeParse(bizData.websiteMaintenance) || 0);
    const finance = safeParse(bizData.workingCapitalInterest) || 0;
    const repairs = (safeParse(bizData.factoryRepairsMaintenance) || 0) + (safeParse(bizData.officeMaintenance) || 0);

    const totalOpEx = distribution + salaries + admin + marketing + finance + repairs;
    const netProfitBeforeTax = grossProfit - totalOpEx;
    const netMargin = turnover > 0 ? (netProfitBeforeTax / turnover) * 100 : 0;

    // 3. Financial Safety & Debt
    const loanEMI = safeParse(bizExp.loanEMIMachinery) || 0;
    const annualEMI = loanEMI * 12;
    const dtiRatio = turnover > 0 ? (annualEMI / turnover) * 100 : 0;

    const reserveFund = safeParse(bizExp.businessReserveFund) || 0;
    const reserveRatio = turnover > 0 ? (reserveFund / turnover) * 100 : 0;
    const monthlyOpOutflow = (totalCOGS + totalOpEx) / 12;
    const liquidityRunway = monthlyOpOutflow > 0 ? (reserveFund / monthlyOpOutflow) : 0;

    // DIAGNOSIS LABELS
    let grossDiag = { result: "Healthy", color: "green" };
    if (grossMargin < 15) grossDiag = { result: "Critical", color: "red" };
    else if (grossMargin < 20) grossDiag = { result: "Moderate", color: "orange" };
    else if (grossMargin >= 40) grossDiag = { result: "Excellent", color: "green" };

    let netDiag = { result: "Healthy", color: "green" };
    if (netMargin < 5) netDiag = { result: "Critical", color: "red" };
    else if (netMargin < 8) netDiag = { result: "Watch", color: "orange" };
    else if (netMargin >= 15) netDiag = { result: "Excellent", color: "green" };

    let costExitDiag = { result: "Healthy", color: "green" };
    const costExitRatio = turnover > 0 ? (totalOpEx / turnover) * 100 : 0;
    if (costExitRatio > 25) costExitDiag = { result: "High (Risky)", color: "red" };
    else if (costExitRatio > 20) costExitDiag = { result: "Attention", color: "orange" };
    else if (costExitRatio < 15) costExitDiag = { result: "Excellent", color: "green" };

    let debtDiag = { result: "Healthy", color: "green" };
    if (dtiRatio > 20) debtDiag = { result: "High", color: "red" };
    else if (dtiRatio > 10) debtDiag = { result: "Watch", color: "orange" };

    let reserveDiag = { result: "Adequate", color: "green" };
    if (reserveRatio < 2) reserveDiag = { result: "Gap", color: "red" };
    else if (reserveRatio < 5) reserveDiag = { result: "Building", color: "orange" };

    let runwayDiag = { result: "Strong", color: "green" };
    if (liquidityRunway < 2) runwayDiag = { result: "Low", color: "red" };
    else if (liquidityRunway < 6) runwayDiag = { result: "Improving", color: "orange" };

    // ROADMAP
    const recommendations = [];
    if (grossMargin < 20) recommendations.push({ category: "Production", priority: "High", action: "Optimize Procurement", details: "Review raw material costs and production efficiencies to improve gross margin." });
    if (reserveRatio < 5) recommendations.push({ category: "Liquidity", priority: "High", action: "Build Reserve Fund", details: `Target 5% of turnover (₹${(turnover * 0.05).toLocaleString()}) as a permanent reserve.` });
    if (liquidityRunway < 6) recommendations.push({ category: "Safety", priority: "Medium", action: "Operations Runway", details: "Aim for 6 months of operating costs in liquid reserves." });

    return {
        metrics: {
            turnover,
            monthlyRevenue,
            grossProfit,
            grossMargin,
            netProfitBeforeTax,
            netMargin,
            costExitRatio,
            dtiRatio,
            reserveRatio,
            lifeInsurancePremium: safeParse(bizExp.termInsuranceOwner) || 0,
            healthInsurancePremium: safeParse(bizExp.healthInsuranceOwner) || 0,
            liquidityRunway,
            totalCOGS,
            totalOpEx,
            monthlyProfit: netProfitBeforeTax / 12,
            netWorth,
            lifeInsuranceCover: safeParse(bizExp.termInsuranceOwner) || 0,
            healthInsuranceCover: safeParse(bizExp.healthInsuranceCover) || safeParse(bizExp.healthInsuranceOwner) || 0
        },
        diagnosis: {
            grossMargin: { value: grossMargin.toFixed(1) + "%", result: grossDiag.result, status: grossDiag.result, color: grossDiag.color, benchmark: "Healthy:\n>40%", priority: grossDiag.result === 'Excellent' ? 'LOW' : 'HIGH' },
            netMargin: { value: netMargin.toFixed(1) + "%", result: netDiag.result, status: netDiag.result, color: netDiag.color, benchmark: "Target:\n>15%", priority: netDiag.result === 'Excellent' ? 'LOW' : 'HIGH' },
            costExit: { value: costExitRatio.toFixed(1) + "%", result: costExitDiag.result, status: costExitDiag.result, color: costExitDiag.color, benchmark: `Target:\n₹${Math.round(monthlyRevenue * 0.15).toLocaleString()}`, priority: costExitDiag.result === 'Excellent' ? 'LOW' : 'HIGH' },
            reserve: { value: reserveRatio.toFixed(1) + "%", result: reserveDiag.result, status: reserveDiag.result, color: reserveDiag.color, benchmark: `Target:\n₹${Math.round(turnover * 0.05).toLocaleString()}`, priority: reserveDiag.result === 'Adequate' ? 'LOW' : 'URGENT' },
            debt: { value: dtiRatio.toFixed(1) + "%", result: debtDiag.result, status: debtDiag.result, color: debtDiag.color, benchmark: "Limit:\n<10%", priority: debtDiag.result === 'Healthy' ? 'LOW' : 'HIGH' },
            runway: { value: liquidityRunway.toFixed(1) + " Mo", result: runwayDiag.result, status: runwayDiag.result, color: runwayDiag.color, benchmark: `Safe:\n₹${Math.round(totalOpEx / 2).toLocaleString()} (6m)`, priority: runwayDiag.result === 'Strong' ? 'LOW' : 'CRITICAL' }
        },
        grades: {
            profitability: netMargin > 10 ? "A" : (netMargin > 5 ? "B" : "C"),
            costControl: costExitRatio < 15 ? "A" : "B",
            liquidity: reserveRatio > 4 ? "A" : "B",
            debt: dtiRatio < 5 ? "A" : "B",
            overall: Math.round(((netMargin / 15) * 40) + ((reserveRatio / 5) * 30) + (costExitRatio < 20 ? 30 : 10))
        },
        recommendations
    };
};
